import { Component, OnInit, inject, ViewChild, TemplateRef, signal, computed, ChangeDetectionStrategy, effect, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { PeriodService, periodKey } from '../../../billing-management/services/period.service';
import { BillingService } from '../../../billing-management/services/billing.service';
import { AutomatizePeriodComponent } from '../../components/automatize-period/automatize-period.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { PermissionService } from '@services/core/permission.service';
import { AppSettings } from '@services/settings/app-settings.service';
import { PageHeaderComponent } from 'app/core/components/page-header/page-header.component';
import { ListCardComponent } from 'app/core/components/list-card/list-card.component';
import Swal from 'sweetalert2';

const MONTHS = [
  { id: 1, name: 'Enero' }, { id: 2, name: 'Febrero' }, { id: 3, name: 'Marzo' },
  { id: 4, name: 'Abril' }, { id: 5, name: 'Mayo' }, { id: 6, name: 'Junio' },
  { id: 7, name: 'Julio' }, { id: 8, name: 'Agosto' }, { id: 9, name: 'Septiembre' },
  { id: 10, name: 'Octubre' }, { id: 11, name: 'Noviembre' }, { id: 12, name: 'Diciembre' }
];

@Component({
  selector: 'app-period-management',
  standalone: true,
  imports: [CommonModule, FormsModule, NgSelectModule, AutomatizePeriodComponent, PageHeaderComponent, ListCardComponent],
  providers: [BillingService],
  templateUrl: './period-management.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./period-management.component.scss']
})
export class PeriodManagementComponent implements OnInit {
  private billingService = inject(BillingService);
  private periodService = inject(PeriodService);
  private modalService = inject(NgbModal);
  readonly perm = inject(PermissionService);
  public appSettings = inject(AppSettings);

  isLoading = signal(false);
  isClosing = signal(false);
  allPeriods = computed(() => this.periodService.periods());
  selectedStartId = signal<number | null>(null);
  selectedEndId = signal<number | null>(null);

  months = MONTHS;
  placeholderRows = [1, 2, 3, 4, 5, 6];

  periodOptions = computed(() => this.allPeriods()
    .slice()
    .sort((a, b) => periodKey(a) - periodKey(b))
    .map(p => ({
      ...p,
      displayName: `${p.year} - ${String(p.period).padStart(2, '0')} ${this.getMonthName(p.period)}`
    })));

  endPeriodOptions = computed(() => {
    const startVal = this.periodValue(this.periodById(this.selectedStartId()));
    return this.periodOptions().filter(p => this.periodValue(p) >= startVal);
  });

  openPeriod = computed(() => {
    const active = this.allPeriods()
      .filter(p => p.statusSlug === 'ACTIVE')
      .sort((a, b) => this.periodValue(b) - this.periodValue(a));
    return active[0] ?? null;
  });

  periods = computed(() => {
    const startVal = this.periodValue(this.periodById(this.selectedStartId()));
    const endVal = this.periodValue(this.periodById(this.selectedEndId()));
    if (!startVal || !endVal) return [];
    return this.periodOptions().filter(p => {
      const value = this.periodValue(p);
      return value >= startVal && value <= endVal;
    });
  });

  nextToOpen = computed(() => {
    if (this.openPeriod()) return null;
    const pending = this.allPeriods()
      .filter(p => p.statusSlug === 'PENDING_ENTITY')
      .sort((a, b) => this.periodValue(a) - this.periodValue(b));
    return pending[0] ?? null;
  });

  @ViewChild('previewDetail') previewDetail!: TemplateRef<any>;
  @ViewChild('automatizeModal') automatizeModal!: TemplateRef<any>;
  isLoadingGen = false;
  generateCollectionsData: any[] = [];
  totalPeriodContribution = 0;
  periodClose: any = null;
  sendMailConfirm = true;
  private focusAfterReload: { id?: number; year?: number; period?: number } | null = null;

  constructor() {
    effect(() => {
      const list = this.periodService.periods();
      const rev = this.periodService.revision();
      if (!list.length && !rev) return;
      untracked(() => {
        const focus = this.focusAfterReload;
        this.focusAfterReload = null;
        this.syncRange(focus);
        this.isLoading.set(false);
      });
    });
  }

  ngOnInit(): void {
    if (!this.periodService.periods().length) this.periodService.reload();
  }

  refresh(focusPeriod?: { id?: number; year?: number; period?: number } | null): void {
    this.focusAfterReload = focusPeriod ?? null;
    this.isLoading.set(true);
    this.periodService.reload();
  }

  onStartChange(id: number): void {
    this.selectedStartId.set(id);
    const startVal = this.periodValue(this.periodById(id));
    const endVal = this.periodValue(this.periodById(this.selectedEndId()));
    if (!endVal || endVal < startVal) this.selectedEndId.set(id);
  }

  onEndChange(id: number): void {
    this.selectedEndId.set(id);
  }

  periodValue(p: { year?: number; period?: number } | null | undefined): number {
    if (!p || p.year == null || p.period == null) return 0;
    return Number(p.year) * 100 + Number(p.period);
  }

  periodById(id: number | null | undefined): any | null {
    if (id == null) return null;
    return this.allPeriods().find(p => Number(p.id) === Number(id)) ?? null;
  }

  isYearStart(period: any, index: number): boolean {
    if (index === 0) return true;
    return Number(this.periods()[index - 1]?.year) !== Number(period?.year);
  }

  private syncRange(focusPeriod?: { id?: number; year?: number; period?: number } | null): void {
    const options = this.allPeriods()
      .slice()
      .sort((a, b) => this.periodValue(a) - this.periodValue(b));
    if (options.length === 0) {
      this.selectedStartId.set(null);
      this.selectedEndId.set(null);
      return;
    }

    const open = options
      .filter(p => p.statusSlug === 'ACTIVE')
      .sort((a, b) => this.periodValue(b) - this.periodValue(a))[0];
    const focus = focusPeriod
      ? options.find(p => Number(p.id) === Number(focusPeriod.id))
        ?? options.find(p => Number(p.year) === Number(focusPeriod.year) && Number(p.period) === Number(focusPeriod.period))
      : null;
    const anchor = focus ?? open ?? options[options.length - 1];
    const prevYearStart = options.find(p => Number(p.year) === Number(anchor.year) - 1 && Number(p.period) === 1);
    const yearStart = options.find(p => Number(p.year) === Number(anchor.year) && Number(p.period) === 1);
    const yearEnd = [...options].reverse().find(p => Number(p.year) === Number(anchor.year));

    const start = prevYearStart ?? yearStart ?? options[0];
    const end = yearEnd ?? anchor;
    this.selectedStartId.set(start.id);
    this.selectedEndId.set(end.id);
  }

  periodLabel(p: { year?: number; period?: number } | null | undefined): string {
    if (!p?.year || p.period == null) return '';
    return `${String(p.period).padStart(2, '0')}/${p.year}`;
  }

  getMonthName(m: number): string {
    return this.months.find(x => x.id === m)?.name ?? '';
  }

  closeDate(period: any): Date | string | null {
    return period?.dateClose || period?.closingDate || null;
  }

  closeModeLabel(period: any): string | null {
    const mode = String(period?.closeMode || '').toUpperCase();
    if (mode === 'AUTOMATIC') return 'Automático';
    if (mode === 'MANUAL') return 'Manual';
    return null;
  }

  isCurrent(period: any): boolean {
    const open = this.openPeriod();
    return !!open && Number(open.id) === Number(period?.id);
  }

  isNextToOpen(period: any): boolean {
    const next = this.nextToOpen();
    return !!next && Number(next.id) === Number(period?.id);
  }

  followingPeriod(period: any): { year: number; period: number } | null {
    if (!period) return null;
    let month = Number(period.period) + 1;
    let year = Number(period.year);
    if (month > 12) {
      month = 1;
      year += 1;
    }
    return { year, period: month };
  }

  openAutomatize(): void {
    this.modalService.open(this.automatizeModal, { backdrop: 'static', centered: true });
  }

  onAutomatizeRan(period: { id?: number; year?: number; period?: number } | null, dismiss: () => void): void {
    dismiss();
    this.refresh(period);
  }

  openPreviewDetails(period: any): void {
    this.periodClose = period;
    this.generateCollectionsData = [];
    this.totalPeriodContribution = 0;
    this.modalService.open(this.previewDetail, { backdrop: 'static', size: 'lg' });
    this.isLoadingGen = true;
    this.billingService.generate(period.id).subscribe({
      next: () => this.loadCloseSummary(period.id),
      error: (err) => {
        this.isLoadingGen = false;
        Swal.fire('Error', 'No se pudieron generar los cobros: ' + (err.error?.error || err.message), 'error');
      }
    });
  }

  private loadCloseSummary(periodId: number): void {
    this.periodService.getSummaryByEntity(periodId).subscribe({
      next: (res) => {
        this.generateCollectionsData = res || [];
        this.totalPeriodContribution = this.generateCollectionsData
          .reduce((s, r) => s + Number(r.totalMonthlyContribution || 0), 0);
        this.isLoadingGen = false;
      },
      error: () => {
        this.isLoadingGen = false;
        Swal.fire('Error', 'No se pudo cargar el resumen de cobros.', 'error');
      }
    });
  }

  confirmClose(): void {
    if (!this.periodClose?.id || this.isClosing()) return;
    this.isClosing.set(true);
    Swal.fire({
      title: 'Cerrando periodo...',
      text: 'Se envían los correos, se cierra el mes y se abre el siguiente con sus cobros. Puede tardar unos minutos.',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading()
    });

    this.periodService.bulkNotify(this.periodClose.id, this.sendMailConfirm).subscribe({
      next: (res) => {
        this.isClosing.set(false);
        const opened = res?.openedPeriod;
        const text = res?.message || (opened
          ? `Se cerró el periodo y se abrió ${this.periodLabel(opened)}.`
          : 'Periodo cerrado correctamente.');
        Swal.fire({
          title: 'Cierre procesado',
          text,
          icon: 'success',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 6000
        });
        this.modalService.dismissAll();
        this.refresh(opened ?? this.periodClose);
      },
      error: (err) => {
        this.isClosing.set(false);
        Swal.fire('Error', err.error?.message || err.error?.error || 'Error al procesar el cierre', 'error');
      }
    });
  }

  openNextMonth(): void {
    if (this.openPeriod()) {
      Swal.fire(
        'Periodo en curso',
        `Ya está abierto ${this.periodLabel(this.openPeriod())}. Cerralo para abrir otro.`,
        'info'
      );
      return;
    }
    Swal.fire({
      title: '¿Abrir el siguiente mes?',
      text: 'Se crea solo ese mes (no el año completo) y se generan sus cobros.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, abrir',
      cancelButtonText: 'Cancelar'
    }).then(r => {
      if (!r.isConfirmed) return;
      this.isLoading.set(true);
      this.periodService.openNextPeriod().subscribe({
        next: (opened) => {
          this.isLoading.set(false);
          Swal.fire({
            title: 'Periodo abierto',
            text: `${this.periodLabel(opened)} quedó como periodo en curso y ya tiene cobros.`,
            icon: 'success',
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 5000
          });
          this.refresh(opened);
        },
        error: (err) => {
          this.isLoading.set(false);
          Swal.fire('Error', err.error?.error || err.error?.message || 'No se pudo abrir el periodo', 'error');
        }
      });
    });
  }

  openCurrentPeriod(period: any): void {
    if (this.openPeriod()) {
      Swal.fire(
        'Periodo en curso',
        `Ya está abierto ${this.periodLabel(this.openPeriod())}. Cerralo para abrir otro.`,
        'info'
      );
      return;
    }
    Swal.fire({
      title: '¿Abrir periodo?',
      text: `Se activará ${this.periodLabel(period)} y se generarán sus cobros. Las transferencias y altas nuevas se asignarán a este periodo.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, abrir',
      cancelButtonText: 'Cancelar'
    }).then(r => {
      if (!r.isConfirmed) return;
      this.periodService.openPeriod(period.id).subscribe({
        next: () => {
          Swal.fire({
            title: 'Periodo abierto',
            text: `${this.periodLabel(period)} quedó como periodo en curso.`,
            icon: 'success',
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 5000
          });
          this.refresh(period);
        },
        error: (err) => Swal.fire('Error', err.error?.error || err.error?.message || 'No se pudo abrir el periodo', 'error')
      });
    });
  }

  generateBilling(period: any): void {
    Swal.fire({
      title: '¿Generar cobros?',
      text: `Esto calculará las primas para ${this.periodLabel(period)}.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, generar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (!result.isConfirmed) return;
      this.isLoading.set(true);
      this.billingService.generate(period.id).subscribe({
        next: (res) => {
          this.isLoading.set(false);
          Swal.fire({
            title: 'Generación completada',
            text: res.message,
            icon: 'info',
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 5000
          });
        },
        error: (err) => {
          this.isLoading.set(false);
          Swal.fire('Error', 'No se pudo generar la facturación: ' + (err.error?.error || err.message), 'error');
        }
      });
    });
  }
}
